import { useEffect, useState } from 'react';
import * as Y from 'yjs';
import type { JSONContent } from '@tiptap/core';
import { docToJSON, loadDoc, loadPageJSON, registerLiveDoc, reindexPage } from '@nexus/workspace';
import { useStore } from './context';

export { docToJSON, jsonToUpdate, loadPageJSON, reindexPage } from '@nexus/workspace';

const SAVE_DELAY_MS = 250;
const INDEX_DELAY_MS = 800;
const VERSION_EVERY_MS = 10 * 60 * 1000;

/**
 * The live Yjs document of a page: loaded from SQLite, and every local change
 * written back (merged, debounced), re-indexed and periodically snapshotted.
 */
export function usePageDoc(pageId: string): Y.Doc | null {
  const store = useStore();
  const [doc, setDoc] = useState<Y.Doc | null>(null);

  useEffect(() => {
    let disposed = false;
    let ydoc: Y.Doc | null = null;
    let pending: Uint8Array[] = [];
    let saveTimer: ReturnType<typeof setTimeout> | undefined;
    let indexTimer: ReturnType<typeof setTimeout> | undefined;
    let lastVersion: number | null = null;
    let indexDue = false;
    let unregister = () => {};

    const index = () => {
      clearTimeout(indexTimer);
      indexDue = false;
      if (ydoc) void reindexPage(store, pageId, docToJSON(ydoc));
    };
    const flush = async () => {
      clearTimeout(saveTimer);
      if (pending.length === 0) return;
      const update = Y.mergeUpdates(pending);
      pending = [];
      await store.appendContent(pageId, update);
      clearTimeout(indexTimer);
      indexDue = true;
      indexTimer = setTimeout(index, INDEX_DELAY_MS);
      lastVersion ??= (await store.lastVersionAt(pageId)) ?? 0;
      if (ydoc && Date.now() - lastVersion > VERSION_EVERY_MS) {
        lastVersion = Date.now();
        await store.saveVersion(pageId, Y.encodeStateAsUpdate(ydoc));
      }
    };
    const onUpdate = (update: Uint8Array, origin: unknown) => {
      if (origin === 'load') return;
      pending.push(update);
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => void flush(), SAVE_DELAY_MS);
    };
    // Leaving the tab or window: write immediately rather than after the debounce.
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush();
    };

    void loadDoc(store, pageId).then((loaded) => {
      if (disposed) return loaded.destroy();
      ydoc = loaded;
      ydoc.on('update', onUpdate);
      unregister = registerLiveDoc(pageId, ydoc);
      // Catch changes written while we were loading (Yjs applies updates idempotently).
      void store.loadContent(pageId).then((updates) => {
        if (!disposed && ydoc)
          Y.transact(ydoc, () => updates.forEach((u) => Y.applyUpdate(ydoc!, u)), 'load');
      });
      document.addEventListener('visibilitychange', onHide);
      setDoc(ydoc);
    });

    return () => {
      disposed = true;
      unregister();
      document.removeEventListener('visibilitychange', onHide);
      const d = ydoc;
      if (!d) return;
      d.off('update', onUpdate);
      const needsIndex = pending.length > 0 || indexDue;
      const json = docToJSON(d);
      void flush().then(() => {
        clearTimeout(indexTimer);
        if (needsIndex) void reindexPage(store, pageId, json);
      });
      d.destroy();
      setDoc(null);
    };
  }, [store, pageId]);

  return doc;
}

/** Read-only content of another page (for embeds), refreshed when it changes. */
export function usePageJSON(pageId: string | null): JSONContent | null | undefined {
  const store = useStore();
  const [loaded, setLoaded] = useState<{ id: string; json: JSONContent } | null>(null);
  useEffect(() => {
    if (!pageId) return;
    let cancelled = false;
    const load = () =>
      void loadPageJSON(store, pageId).then((json) => {
        if (!cancelled) setLoaded({ id: pageId, json });
      });
    load();
    const off = store.subscribe(load, ['content']);
    return () => {
      cancelled = true;
      off();
    };
  }, [store, pageId]);
  if (!pageId) return null;
  return loaded?.id === pageId ? loaded.json : undefined;
}
